/**
 * Utility functions for capturing Meta (Facebook) attribution data
 * for use with Meta Conversions API (CAPI) server-side events.
 */

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = name => {
  if (typeof document === 'undefined') {
    return null;
  }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }

  return null;
};

/**
 * Get the fbclid parameter from the URL query string or sessionStorage
 * @returns {string|null} fbclid value or null if not found
 */
export const getFbclid = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  // Check current URL
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get('fbclid');

  if (fbclid) {
    // Store it in sessionStorage for later use
    try {
      sessionStorage.setItem('_fbclid', fbclid);
    } catch (e) {
      // Ignore sessionStorage errors (private browsing, etc.)
    }
    return fbclid;
  }

  // Check sessionStorage
  try {
    return sessionStorage.getItem('_fbclid');
  } catch (e) {
    return null;
  }
};

/**
 * Build the fbc (Facebook Click) value from fbclid
 * Format: fb.1.<timestampMs>.<fbclid>
 * @param {string} fbclid - The Facebook click ID from URL parameter
 * @returns {string} Formatted fbc value
 */
export const buildFbc = fbclid => {
  if (!fbclid) {
    return null;
  }

  const timestampMs = Date.now();
  return `fb.1.${timestampMs}.${fbclid}`;
};

/**
 * Capture Meta attribution data for CAPI events
 * This should be called when initiating a transaction to ensure
 * attribution data is available for server-side Meta CAPI events.
 *
 * @returns {Object|null} Meta attribution object or null if no data available
 * @property {string} fbp - Facebook Browser ID (_fbp cookie)
 * @property {string} fbc - Facebook Click ID (_fbc cookie or built from fbclid)
 * @property {string} clientUserAgent - Browser user agent string
 */
export const captureMetaAttribution = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  const attribution = {};

  // Get _fbp cookie (Facebook Browser ID)
  const fbp = getCookie('_fbp');
  if (fbp) {
    attribution.fbp = fbp;
  }

  // Get _fbc cookie or build from fbclid
  let fbc = getCookie('_fbc');
  if (!fbc) {
    const fbclid = getFbclid();
    if (fbclid) {
      fbc = buildFbc(fbclid);
    }
  }
  if (fbc) {
    attribution.fbc = fbc;
  }

  // Get user agent
  if (navigator.userAgent) {
    attribution.clientUserAgent = navigator.userAgent;
  }

  // Only return if we have at least one attribution field
  return Object.keys(attribution).length > 0 ? attribution : null;
};
